import { type HTMLAttributes, useMemo } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import type { ICommonSearchParams } from "@/shared/hooks/use-route-match.ts"
import { useRouteMatch } from "@/shared/hooks/use-route-match.ts"
import "./style.scss"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils"
import { MoveLeft } from "lucide-react"

export interface ICrumb {
  path: string
  label: string
  i18Key: string | undefined
  paramKey: string | undefined
  keepSearchParams?: (string | ICommonSearchParams)[]
}

interface IProps extends HTMLAttributes<HTMLDivElement> {
  endCrumbs?: ICrumb[]
}

export const Breadcrumb = ({ endCrumbs, ...props }: IProps) => {
  // Helpers
  const { t } = useTranslation("sidebar")
  const { t: tCommon } = useTranslation("common")
  const params = useParams()
  const [searchParams] = useSearchParams()
  const matches = useRouteMatch()
  const navigate = useNavigate()

  // Functions
  const matchData: ICrumb[] = useMemo(
    () =>
      matches
        .filter(match => (match.handle?.label || match.handle?.i18Key || match.handle?.paramCallback) != null)
        .map(match => ({
          path: match.pathname,
          label: match.handle?.label,
          i18Key: match.handle?.i18Key,
          paramKey: match.handle.paramCallback?.(params),
          keepSearchParams: match.handle.keepSearchParams
        })),
    [matches, params]
  )

  const all = matchData.concat(endCrumbs || [])

  return (
    <div className="flex flex-col gap-2">
      <div {...props} className={cn("breadcrumb", props.className)}>
        {all.map((link, index) => (
          <div
            className="breadcrumb__link"
            onClick={() => {
              if (index === all.length - 1) return
              const keys = link.keepSearchParams
              const nextParams = new URLSearchParams()
              keys?.forEach(key => {
                const value = searchParams.get(key)
                if (value) nextParams.set(key, value)
              })

              navigate(link.path + "?" + nextParams.toString())
            }}
            // onClick={() => && navigate((link.path ?? "dashbaord") + `?${searchParams.toString()}`)}
            key={index}
          >
            <span
              style={{
                animationDuration: ".06s"
              }}
              className="no-underline underline-offset-8 transition-all hover:underline hover:underline-offset-4"
            >
              {t(link?.i18Key || link.label || link.paramKey || "-")}
            </span>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="flex items-center text-ui-foreground-soft gap-2 hover:text-ui-foreground-base transition-all duration-200"
        onClick={() => navigate(-1)}
      >
        <MoveLeft /> {tCommon("back")}
      </button>
    </div>
  )
}
